import React from "react";
import { Link } from "react-router-dom";
import {
  LinkIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";

function AboutPage() {
  const features = [
    {
      icon: LinkIcon,
      title: "Gelişmiş Link Kısaltma",
      description:
        "URL'lerinizi hızlı ve güvenli bir şekilde kısaltın. Custom kod desteği ve meta veri çekme özelliği.",
    },
    {
      icon: CurrencyDollarIcon,
      title: "Ülke Bazlı Kazanç",
      description:
        "Her ülke için farklı CPM oranları. ABD $2.50, İngiltere $2.20, Türkiye $0.80 ve daha fazlası.",
    },
    {
      icon: UserGroupIcon,
      title: "Referans Sistemi",
      description:
        "Arkadaşlarınızı davet edin ve onların kazançlarından %10 komisyon alın.",
    },
    {
      icon: ShieldCheckIcon,
      title: "Güvenli & Güvenilir",
      description:
        "SSL şifreleme, güvenli ödeme sistemi ve 99.9% uptime garantisi.",
    },
    {
      icon: ChartBarIcon,
      title: "Detaylı Analytics",
      description:
        "Link performansınızı analiz edin. Ülke, cihaz ve zaman bazlı istatistikler.",
    },
  ];

  const stats = [
    { value: "50M+", label: "Kısaltılan Link" },
    { value: "10K+", label: "Aktif Kullanıcı" },
    { value: "$250K+", label: "Ödenen Tutar" },
    { value: "150+", label: "Desteklenen Ülke" },
  ];

  return (
    <div className="py-12">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            KsLnk Hakkında
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            2024 yılında kurulan KsLnk, link kısaltma sektörüne yenilikçi bir
            yaklaşım getirmektedir. Sadece link kısaltma değil, aynı zamanda
            para kazanma imkânı sunan platformumuz ile milyonlarca kullanıcıya
            hizmet vermeyi hedefliyoruz.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">
                {stat.value}
              </div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Misyonumuz
              </h2>
              <div className="space-y-4 text-gray-600 text-lg">
                <p>
                  KsLnk olarak amacımız, internet kullanıcılarının günlük
                  hayatlarında kullandıkları linkleri değerli bir gelir
                  kaynağına dönüştürmektir.
                </p>
                <p>
                  Modern teknolojiler kullanarak geliştirdiğimiz platformumuz,
                  kullanıcı deneyimini en üst seviyede tutarken, adil ve şeffaf
                  bir kazanç sistemi sunmaktadır.
                </p>
                <p>
                  Vizyonumuz, dünya çapında en güvenilir ve kazançlı link
                  kısaltma platformu olmaktır.
                </p>
              </div>
            </div>
            <div className="mt-12 lg:mt-0">
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="text-2xl font-bold text-primary-600 mb-2">
                    99.9%
                  </div>
                  <div className="text-gray-600">Uptime Garantisi</div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="text-2xl font-bold text-green-600 mb-2">
                    24/7
                  </div>
                  <div className="text-gray-600">Müşteri Desteği</div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="text-2xl font-bold text-blue-600 mb-2">
                    SSL
                  </div>
                  <div className="text-gray-600">Güvenli Bağlantı</div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="text-2xl font-bold text-purple-600 mb-2">
                    API
                  </div>
                  <div className="text-gray-600">Developer Desteği</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Özelliklerimiz
            </h2>
            <p className="text-xl text-gray-600">
              Modern teknoloji ile geliştirilmiş özelliklermiz
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-primary-600 to-purple-700 py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-6">
            Hemen Başlayın!
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Hesabınızı oluşturun ve linklerinizle para kazanmaya başlayın
          </p>
          <Link to="/register" className="btn btn-warning btn-xl">
            Ücretsiz Hesap Oluştur
            <ArrowRightIcon className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
